/**
 * tabs, the Building page's tab-routing contract.
 *
 * The shipped LL97 mechanism, in two halves that must agree: `validateSearch`
 * on the file route drops an unrecognised `?tab=` to `undefined`
 * (`routes/portal/buildings/$buildingId.tsx`,
 * `routes/portal/buildings_/$buildingId/ll97.tsx`), and a pure normalizer
 * turns "no tab / an unknown tab" into THE DEFAULT TAB
 * (`readLl97ProcessSearch`, `pages/Portal/Ll97Process/dispatch.ts`). Both
 * halves here close over ONE list and ONE guard; the shipped pair spells its
 * list twice, and a tab legal in one half and illegal in the other renders a
 * blank surface. The third half, WRITING the URL, is `useBuildingTab` below,
 * resolving through `readBuildingDetailSearch` so the tested and shipped
 * functions are one.
 *
 * TRAP: the hook imports React and the router at module scope and ES imports
 * hoist, so importing this for the parser alone pulls both. Do not add a
 * genuinely heavy import here.
 *
 *   #<an anchor this page owns>       → the tab whose BODY holds it
 *   ?tab=codes|exposure|case|record   → that tab
 *   ?code=<code id>                   → Codes, with that code's card open (C7)
 *   ?tab absent                       → "codes"
 *   ?tab=<anything else>              → "codes", and the param is dropped
 *                                       from the URL by `validateSearch`
 *                                       rather than round-tripped
 *
 * "codes" is the default because a bare `/portal/buildings/<id>` was the
 * applicable-codes board before the tab strip, so every predating link,
 * bookmark and test still lands where it did.
 *
 * ── THE HASH OUTRANKS `?tab=` ─────────────────────────────────────────────
 *
 * Three SHIPPED links predate the strip and name an element inside a tab body:
 * `TrackerSidebar.tsx:931` → `#managed-cases`, `TrackerSidebar.tsx:564` →
 * `#case-<id>`, and Wins on `RecordTab` → `#applicable-codes` (Drivers, Aging
 * and ThisMonthStrip linked here too until PR-18 retired all three). The
 * sidebar is FROZEN BY RULING, so the ANCHOR selects the tab: it names an
 * ELEMENT, the more specific request, where a disagreeing `?tab=` was an
 * earlier click or hand-typed.
 *
 * TRAP: on 1.147.1 a `<Link>` setting only `hash` does NOT inherit the search.
 * `applySearchMiddleware`'s terminal `if (!dest.search) return {}`
 * (`router.js:1360-1368`) DROPS it. The conflicting URL is one THIS page
 * mints: `goToCase` passes `search: (prev) => prev` on purpose (a functional
 * update IS preserved where a missing `search` is not), so "Follow the case →"
 * from `?tab=exposure` writes `?tab=exposure#case-<id>`; give `?tab=`
 * precedence and it lands on Exposure and scrolls nowhere.
 *
 * TRAP: selecting a tab must DROP the hash, or a reader who arrived at
 * `#managed-cases` can never leave the Case tab. `goToTab` passes no `hash`
 * and router-core reads that as CLEAR, not KEEP: `dist/esm/router.js:367`,
 * `dest.hash ? … : void 0`, then `hashStr = hash ? '#'+hash : ''`. Pinned
 * below.
 */

import { useCallback } from "react";
import { useNavigate, useRouter } from "@tanstack/react-router";

import { openCaseCount } from "@/components/portal/caseCounts";

/** The four tabs the design draws, in the order it draws them. */
export type BuildingTab = "codes" | "exposure" | "case" | "record";

export const BUILDING_TABS: readonly BuildingTab[] = [
  "codes",
  "exposure",
  "case",
  "record",
] as const;

/** What a bare `/portal/buildings/<id>` renders. */
export const DEFAULT_BUILDING_TAB: BuildingTab = "codes";

/** The tab strip's id; a tab swap scrolls it back into view. */
export const BUILDING_TABS_ID = "building-tabs";

/**
 * The tab of the one create path for a case, the cluster cards' "Treat as one
 * problem" (`BuildingCaseStates.dc.html`). TRAP, Oct 25: no live surface renders
 * those cards; the Case board's empty state and the Record drawer still send here.
 */
export const VIOLATION_PATTERNS_TAB: BuildingTab = "exposure";

/** Tab-strip labels. Sentence case, per the boards. */
export const BUILDING_TAB_LABELS: Record<BuildingTab, string> = {
  codes: "Codes",
  exposure: "Exposure",
  case: "Case",
  record: "Record",
};

/**
 * Open cases, the ONE formula behind the Case tab's count and the count the
 * case board's header states 60px below it. Defined component-side in
 * `components/portal/caseCounts`, re-exported here so the tab contract reads
 * in one file. Structurally typed (`{ status }`) rather than importing
 * `ViolationCase`: the route imports THIS module eagerly, and pulling
 * `queries/violationCasesQueries` in for one string field would drag axios and
 * the query client into that chunk.
 */
export { openCaseCount } from "@/components/portal/caseCounts";

/**
 * The tab strip's ONE count: a bare open-case number on Case (Leo,
 * 2026-09-28, cutting "10 REQUIRED" off Codes and "OPEN" off Case).
 *
 * A REAL ZERO IS STILL DECLINED: "Case 0" is true of a building that never
 * had a case and of one whose three closed last week, and reads as the first
 * in both. The Case board's empty state says which.
 *
 * KNOWN GAP: a building whose cases are ALL resolved renders no count while
 * its board lists them. Unreachable today; nothing in the backend writes
 * `ViolationCase.status = RESOLVED`.
 */
export const buildingTabCounts = (
  cases?: readonly { status: string }[] | null,
): Partial<Record<BuildingTab, string>> => {
  const counts: Partial<Record<BuildingTab, string>> = {};
  const open = openCaseCount(cases);
  if (open !== undefined && open > 0) counts.case = `${open}`;
  return counts;
};

/**
 * Case shows only when the building has a case (Leo, 2026-10-01). `null`
 * while loading hides it too, so the strip never flashes a dead tab.
 */
export const visibleBuildingTabs = (
  cases?: readonly unknown[] | null,
): readonly BuildingTab[] =>
  cases && cases.length > 0
    ? BUILDING_TABS
    : BUILDING_TABS.filter((t) => t !== "case");

/**
 * The ONE guard both halves of the contract read. A tab is legal in the
 * URL exactly when it is legal in the render.
 */
export const isBuildingTab = (v: unknown): v is BuildingTab =>
  typeof v === "string" && (BUILDING_TABS as readonly string[]).includes(v);

/**
 * THE ANCHOR MAP: every `id` a shipped link points at on this page, and which
 * tab body renders it. An anchor not here is one no inbound link can reach.
 *
 *   managed-cases       BuildingCasesSection's root    → Case
 *   case-<id>           one case block (and its popover,
 *                       `case-citations-<id>`, which is
 *                       inside the same block)         → Case
 *   applicable-codes    CodesBoard's root              → Codes
 *   violation-patterns  the cluster cards (none render
 *                       today, Oct 25)                 → Exposure
 *   exposure-row-<code> one ranked or below-the-line
 *                       row on ExposureBoard           → Exposure
 *   hpd-violation-<id>  one HPD row (HpdViolationsSection) → Record
 *
 * TRAP: `applicable-codes` answers the literal `"codes"`, THE TAB WHOSE BODY
 * RENDERS IT (`CodesBoard.tsx:168`, `<section id="applicable-codes">`), never
 * `DEFAULT_BUILDING_TAB`, though they are the same string today. Change the
 * default to `"exposure"` and every `#applicable-codes` link (Wins on
 * `RecordTab`, sole caller since PR-18 retired Drivers, Aging and
 * ThisMonthStrip) resolves to a body without the element, while a test written
 * `toBe(DEFAULT_BUILDING_TAB)` moves with it and stays green. The tests below
 * name the literal tab for the same reason.
 */
export const MANAGED_CASES_ANCHOR = "managed-cases";
export const CASE_ANCHOR_PREFIX = "case-";
export const APPLICABLE_CODES_ANCHOR = "applicable-codes";

/**
 * SELECTING THE TAB IS NO LONGER ENOUGH: on the Exposure body the cluster
 * cards mount LAST, below both axis cards, the attribution panel, the pattern
 * table, the projection panel and the per-source ledger tables. Without this
 * anchor, "how to start a case" and the Record drawer's "Request help" land at
 * the top of Exposure with the CTA screens down, a dead end that throws no
 * error and fails no test.
 */
export const VIOLATION_PATTERNS_ANCHOR = "violation-patterns";

/** One charge code's row on Exposure. Minted by the Dashboard's By charge code
 *  jump; the slice id is the trimmed code, as `by_charge` keys it. */
export const EXPOSURE_ROW_ANCHOR_PREFIX = "exposure-row-";
export const exposureRowAnchor = (code: string): string =>
  `${EXPOSURE_ROW_ANCHOR_PREFIX}${code}`;

/** One HPD violation row on Record (H1), keyed by HPD's violationid. */
export const HPD_VIOLATION_ANCHOR_PREFIX = "hpd-violation-";
export const hpdViolationAnchor = (violationId: string): string =>
  `${HPD_VIOLATION_ANCHOR_PREFIX}${violationId}`;

/** One code tile on Codes, per code and BIN so lot-mate tiles never clash. */
export const CODE_TILE_ANCHOR_PREFIX = "code-";
export const codeTileAnchor = (codeId: string, bin?: string | null): string =>
  `${CODE_TILE_ANCHOR_PREFIX}${codeId}${bin ? `-${bin}` : ""}`;

/** A server code id (`hpd_registration`); anything else is dropped. */
export const isCodeParam = (v: unknown): v is string =>
  typeof v === "string" && /^[a-z0-9_]{1,40}$/.test(v);

/**
 * A hash as an element id: no leading `#`, no surrounding space. ONE SPELLING,
 * because this module decides which TAB an anchor selects and `CaseBoard`
 * calls `getElementById` with the same string; spell it twice and a future
 * `decodeURIComponent` (case ids in a hash) lands in only one, so the deep
 * link selects the right tab then scrolls nowhere. Tolerates the `#` because
 * TanStack's `location.hash` is bare and `window.location.hash` carries it.
 */
export const normalizeAnchor = (hash: string | null | undefined): string =>
  (hash ?? "").replace(/^#/, "").trim();

/**
 * Which tab body holds `#<hash>`, or `undefined` for an anchor this page
 * does not own.
 */
export const tabForHash = (
  hash: string | null | undefined,
): BuildingTab | undefined => {
  const anchor = normalizeAnchor(hash);
  if (!anchor) return undefined;
  if (anchor === MANAGED_CASES_ANCHOR || anchor.startsWith(CASE_ANCHOR_PREFIX))
    return "case";
  // The literal tab that OWNS the element, never `DEFAULT_BUILDING_TAB`.
  // See the map above.
  if (anchor === APPLICABLE_CODES_ANCHOR) return "codes";
  // NOT the `DEFAULT_BUILDING_TAB` mistake the map warns about: this constant
  // is DEFINED as the tab hosting the cluster cards, and this element is
  // inside them, so the two move together by construction.
  if (anchor === VIOLATION_PATTERNS_ANCHOR) return VIOLATION_PATTERNS_TAB;
  if (anchor.startsWith(EXPOSURE_ROW_ANCHOR_PREFIX)) return "exposure";
  if (anchor.startsWith(HPD_VIOLATION_ANCHOR_PREFIX)) return "record";
  if (anchor.startsWith(CODE_TILE_ANCHOR_PREFIX)) return "codes";
  return undefined;
};

/** THE resolver: hash first, then the validated `?tab=`, then the default.
 *  The precedence argument is in the module docblock and is load-bearing. */
export const resolveBuildingTab = (
  tab: unknown,
  hash?: string | null,
): BuildingTab =>
  tabForHash(hash) ?? (isBuildingTab(tab) ? tab : DEFAULT_BUILDING_TAB);

/** The render half's shape: a tab is always resolved. */
export interface BuildingDetailSearch {
  tab: BuildingTab;
}

/**
 * Normalize raw route search for rendering. Exported separately from the route
 * (the `readLl97ProcessSearch` idiom) so a unit test pins the contract without
 * a TanStack router. ON THE PRODUCTION PATH: `useBuildingTab` resolves through
 * this, so the "absent / unknown → codes" tests guard the real default.
 */
export const readBuildingDetailSearch = (
  search: unknown,
  hash?: string | null,
): BuildingDetailSearch => {
  const obj = (search ?? {}) as Record<string, unknown>;
  return { tab: resolveBuildingTab(obj.tab, hash) };
};

/** The URL half's shape: an unrecognised tab is absent, not defaulted. */
export interface BuildingDetailRouteSearch {
  tab?: BuildingTab;
  code?: string;
}

/**
 * The route's `validateSearch`. Returns `undefined`, not the default:
 * defaulting HERE stamps `?tab=codes` onto every link into the route and the
 * clean URL meaning "the codes board" stops existing. The default belongs to
 * the render half.
 */
export const validateBuildingDetailSearch = (
  search: Record<string, unknown>,
): BuildingDetailRouteSearch => ({
  tab: isBuildingTab(search?.tab) ? search.tab : undefined,
  code: isCodeParam(search?.code) ? search.code : undefined,
});

/**
 * Selecting the DEFAULT tab writes `undefined`, DROPPING the param, so the
 * clean `/portal/buildings/<id>` keeps meaning the codes board.
 *
 * EVERYTHING ELSE ON THE QUERY STRING RIDES THROUGH the spread below
 * (`search: (prev) => …`, the LL97 `goToTab` idiom). MEASURED live on TanStack
 * Router 1.134: from `?tab=case&pane=identity`, clicking Codes gives
 * `?pane=identity`, then Record gives `?pane=identity&tab=record`. An
 * undeclared param survives `validateSearch` and every tab transition, but
 * NOT the TYPE: `Route.useSearch()` is typed to `BuildingDetailRouteSearch`,
 * so a future deep-link param must be declared in
 * `validateBuildingDetailSearch` before the page can read it.
 */
export const useBuildingTab = (
  buildingId: string,
  tab: BuildingTab | undefined,
  hash?: string | null,
): {
  activeTab: BuildingTab;
  goToTab: (next: BuildingTab) => void;
  /** Deep-link to ONE case block: `#case-<id>` resolves to the Case tab, so
   *  one navigate selects the body AND names the element and Back rewinds
   *  both. The board scrolls to it once the cases render. */
  goToCase: (caseId: string) => void;
  /** Select the tab that hosts the violation patterns, the one create path. */
  goToPatterns: () => void;
  /** `?code=`: the code whose card the Codes tab opens. */
  codeParam: string | null;
  clearCodeParam: () => void;
} => {
  const navigate = useNavigate();
  // TRAP: the route passes only `tab`, and `useSearch` throws outside a
  // RouterProvider (every Page test). `useRouter` returns undefined there.
  // The route re-renders on any search change, so this read stays current.
  const rawCode = (
    useRouter({ warn: false })?.state.location.search as
      | Record<string, unknown>
      | undefined
  )?.code;
  const codeParam = isCodeParam(rawCode) ? rawCode : null;
  // One resolver, shared with the route's own read. Do NOT inline a second
  // `?? DEFAULT_BUILDING_TAB`: `tab` is typed as already-validated, which is
  // what tempts the next reader to drop the fallback.
  const { tab: activeTab } = readBuildingDetailSearch({ tab }, hash);
  const goToTab = useCallback(
    (next: BuildingTab) => {
      navigate({
        to: "/portal/buildings/$buildingId",
        params: { buildingId },
        // Leo 2026-10-01: Back leaves the page, not the tab. Scroll stays put.
        replace: true,
        resetScroll: false,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          tab: next === DEFAULT_BUILDING_TAB ? undefined : next,
          code: undefined,
        }),
        // NO `hash` KEY, AND THAT IS THE POINT: router-core reads an
        // absent/falsy `hash` as CLEAR (`router.js:367`). A tab request must
        // drop the anchor, or a reader who arrived at `#managed-cases` can
        // never select Codes. Pinned in `BuildingPageTabs.test.tsx`.
      });
    },
    [navigate, buildingId],
  );
  const goToCase = useCallback(
    (caseId: string) => {
      navigate({
        to: "/portal/buildings/$buildingId",
        params: { buildingId },
        // Search rides through untouched: the hash outranks `?tab=` anyway, so
        // rewriting the param would only noise the URL and lose Back state.
        search: (prev: Record<string, unknown>) => prev,
        hash: `${CASE_ANCHOR_PREFIX}${caseId}`,
      });
    },
    [navigate, buildingId],
  );
  // WRITES THE ANCHOR, like `goToCase`. `goToTab` CLEARS the hash, so this
  // cannot layer on it; it names the block because the block is last on a long
  // body, and the resolver reads `#violation-patterns` back as Exposure.
  const goToPatterns = useCallback(() => {
    navigate({
      to: "/portal/buildings/$buildingId",
      params: { buildingId },
      search: (prev: Record<string, unknown>) => prev,
      hash: VIOLATION_PATTERNS_ANCHOR,
    });
  }, [navigate, buildingId]);
  const clearCodeParam = useCallback(() => {
    navigate({
      to: "/portal/buildings/$buildingId",
      params: { buildingId },
      replace: true,
      resetScroll: false,
      search: (prev: Record<string, unknown>) => ({ ...prev, code: undefined }),
      hash: true, // keep `#case-…`; an absent key clears it
    });
  }, [navigate, buildingId]);
  return {
    activeTab,
    goToTab,
    goToCase,
    goToPatterns,
    codeParam,
    clearCodeParam,
  };
};
